// 独自プロジェクト形式 (.bpe.zip)。
//
// manifest.json  { format, version, appVersion, createdAt, surfaces: { [pixelId]: { channels, x, y, w, h } }, fonts? }
// document.json  Document
// surfaces/<pixelId>.png  表面の bounds 範囲を PNG (RGBA または グレー) で格納。ZIP level 0
// fonts/<n>.<ext>         ユーザーが読み込んだフォントの原本。同梱フォントやシステムフォントは入れない
//                         (入れられない)。別の PC で開くと字形が変わるため、呼び出し側が警告する
//
// 復元は「manifest → document.json の検証 → 全 PNG のデコードと寸法一致確認」まで終えてから
// 呼び出し側 (documentStore.replaceDocument) に渡す。途中で失敗したら現在の文書に触れない。

import { zip, unzip, type Zippable, type Unzipped } from 'fflate'
import { decode as decodePng } from 'fast-png'
import { encodePngAsync } from '@/workers/clients'
import type { Document } from '@/types/document'
import { validateDocument } from '@/model/validate'
import { referencedPixelIds } from '@/model/layerTree'
import { PixelSurface } from '@/pixels/pixelSurface'
import { pixelStore } from '@/pixels/pixelStore'

export const PROJECT_FORMAT = 'browser-photo-editor'
export const PROJECT_VERSION = 1
export const PROJECT_EXT = '.bpe.zip'

interface SurfaceMeta {
  channels: 1 | 4
  x: number
  y: number
  w: number
  h: number
}

interface FontMeta {
  family: string
  filename: string
  file: string
}

interface Manifest {
  format: string
  version: number
  appVersion: string
  createdAt: string
  surfaces: Record<string, SurfaceMeta>
  /** 埋め込んだフォント。無いこともある (古いファイル / 使っていない) */
  fonts?: FontMeta[]
}

/** 埋め込むフォントの原本 (text/fonts.ts の UserFont と同じ形。ここは DOM に依存させない) */
export interface EmbeddedFont {
  family: string
  filename: string
  data: Uint8Array
}

function zipAsync(files: Zippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => zip(files, { level: 0 }, (err, data) => (err ? reject(err) : resolve(data))))
}

function unzipAsync(data: Uint8Array): Promise<Unzipped> {
  return new Promise((resolve, reject) => unzip(data, (err, out) => (err ? reject(err) : resolve(out))))
}

export interface SaveResult {
  blob: Blob
  /** 保存した時点の文書 (署名の記録用) */
  document: Document
}

export async function createProjectBlob(doc: Document, appVersion: string, fonts: EmbeddedFont[] = []): Promise<SaveResult> {
  const files: Zippable = {}
  const surfaces: Record<string, SurfaceMeta> = {}
  // 文書が参照する表面だけを保存する。欠けていれば失敗 (黙って飛ばさない)。
  // 開始時点の表面参照を先に確保し、エンコードは Worker で並列に行う
  const jobs: Promise<void>[] = []
  for (const id of referencedPixelIds(doc)) {
    const s = pixelStore.get(id)
    if (!s) throw new Error(`ピクセル表面が見つからない: ${id}`)
    const b = s.bounds()
    if (!b) {
      surfaces[id] = { channels: s.channels, x: 0, y: 0, w: 0, h: 0 }
      continue
    }
    surfaces[id] = { channels: s.channels, x: b.x, y: b.y, w: b.w, h: b.h }
    const data = s.readRect(b)
    jobs.push(
      encodePngAsync(b.w, b.h, s.channels, data).then(png => {
        files[`surfaces/${id}.png`] = [png, { level: 0 }]
      })
    )
  }
  await Promise.all(jobs)
  // フォントは圧縮済み (woff2) のこともあるが、ttf/otf は効くので既定の圧縮にする
  const fontMeta: FontMeta[] = fonts.map((f, i) => {
    const ext = f.filename.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toLowerCase() ?? 'ttf'
    const file = `fonts/${i}.${ext}`
    files[file] = [f.data, { level: 6 }]
    return { family: f.family, filename: f.filename, file }
  })
  const manifest: Manifest = { format: PROJECT_FORMAT, version: PROJECT_VERSION, appVersion, createdAt: new Date().toISOString(), surfaces, ...(fontMeta.length ? { fonts: fontMeta } : {}) }
  const enc = new TextEncoder()
  files['manifest.json'] = [enc.encode(JSON.stringify(manifest, null, 2)), { level: 6 }]
  files['document.json'] = [enc.encode(JSON.stringify(doc)), { level: 6 }]
  const bytes = await zipAsync(files)
  return { blob: new Blob([bytes as BlobPart], { type: 'application/zip' }), document: doc }
}

export interface LoadedProject {
  document: Document
  surfaces: PixelSurface[]
  /** 埋め込まれていたフォント。呼び出し側が FontFace として登録する */
  fonts: EmbeddedFont[]
}

export async function loadProjectBlob(blob: Blob): Promise<LoadedProject> {
  const files = await unzipAsync(new Uint8Array(await blob.arrayBuffer()))
  const dec = new TextDecoder()
  const manifestRaw = files['manifest.json']
  const docRaw = files['document.json']
  if (!manifestRaw || !docRaw) throw new Error('manifest.json / document.json がありません')
  const manifest = JSON.parse(dec.decode(manifestRaw)) as Partial<Manifest>
  if (manifest.format !== PROJECT_FORMAT) throw new Error('このアプリのプロジェクトファイルではありません')
  if (manifest.version !== PROJECT_VERSION) throw new Error(`未対応のバージョン: ${String(manifest.version)}`)
  if (!manifest.surfaces || typeof manifest.surfaces !== 'object') throw new Error('manifest.surfaces がありません')

  const surfaces: PixelSurface[] = []
  const ids = new Set<string>()
  for (const [id, meta] of Object.entries(manifest.surfaces)) {
    if (!/^[\w-]+$/.test(id)) throw new Error(`不正な pixelId: ${id}`)
    if (meta.channels !== 1 && meta.channels !== 4) throw new Error(`不正なチャンネル数: ${id}`)
    const s = new PixelSurface(id, meta.channels)
    if (meta.w > 0 && meta.h > 0) {
      const pngRaw = files[`surfaces/${id}.png`]
      if (!pngRaw) throw new Error(`ピクセルデータが欠けています: ${id}`)
      const png = decodePng(pngRaw)
      if (png.width !== meta.w || png.height !== meta.h) throw new Error(`寸法が一致しません: ${id}`)
      if (png.channels !== meta.channels || png.depth !== 8) throw new Error(`形式が一致しません: ${id}`)
      const data = png.data instanceof Uint8Array ? new Uint8ClampedArray(png.data.buffer, png.data.byteOffset, png.data.byteLength) : new Uint8ClampedArray(png.data as ArrayLike<number>)
      s.writeRect({ x: meta.x, y: meta.y, w: meta.w, h: meta.h }, data)
      s.revision = 0
    }
    surfaces.push(s)
    ids.add(id)
  }
  const fonts: EmbeddedFont[] = []
  for (const meta of manifest.fonts ?? []) {
    if (typeof meta?.family !== 'string' || !meta.family || typeof meta.file !== 'string') throw new Error('manifest.fonts が不正です')
    if (!/^fonts\/[\w.-]+$/.test(meta.file)) throw new Error(`不正なフォントのパス: ${meta.file}`)
    const raw = files[meta.file]
    if (!raw) throw new Error(`フォントデータが欠けています: ${meta.family}`)
    fonts.push({ family: meta.family, filename: typeof meta.filename === 'string' ? meta.filename : meta.family, data: raw })
  }
  const document = validateDocument(JSON.parse(dec.decode(docRaw)), ids)
  return { document, surfaces, fonts }
}
